import { UserProfile } from '@amzn/awscat-pattern-library-service-model';
import delay from 'delay';
import { useEffect, useReducer, useRef } from 'react';
import { MplServiceClientFactory } from '../../../api/MplServiceClientFactory';

export interface FetchTechWritersState {
  techWriters: UserProfile[];
  isLoading: boolean;
  error?: string;
}
export interface FetchBarRaisersState {
  barRaisers: UserProfile[];
  isLoading: boolean;
  error?: string;
}

export function useTechWriters() {
  const mplService = MplServiceClientFactory.create();

  const cache = useRef<UserProfile[] | undefined>(undefined);

  const initialState: FetchTechWritersState = {
    techWriters: [],
    isLoading: true,
  };
  const [state, dispatch] = useReducer(
    (s: FetchTechWritersState, newState: Partial<FetchTechWritersState>) => {
      return { ...s, ...newState };
    },
    initialState,
  );

  const controller = new AbortController();

  const loadTechWriters = async () => {
    try {
      dispatch({ isLoading: true });
      let techWriters: UserProfile[];
      if (cache.current) {
        techWriters = cache.current;
      } else {
        const { userProfiles } = await mplService.getTechWriterProfiles(controller);
        // @ts-expect-error TS7006 - strict migration
        techWriters = userProfiles.map((up) => ({ userId: up.userId, name: up.userName }));
      }
      dispatch({ isLoading: false, techWriters });
    } catch (e) {
      if (!controller.signal.aborted) {
        // @ts-expect-error TS18046 - strict migration
        dispatch({ isLoading: false, techWriters: [], error: e.msg });
      }
    }
  };

  useEffect(() => {
    loadTechWriters();
    return () => controller.abort();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return { ...state };
}

export function useBarRaisers(techDomain: string) {
  const mplService = MplServiceClientFactory.create();

  const cache = useRef<UserProfile[] | undefined>(undefined);

  const initialState: FetchBarRaisersState = {
    barRaisers: [],
    isLoading: true,
  };
  const [state, dispatch] = useReducer(
    (s: FetchBarRaisersState, newState: Partial<FetchBarRaisersState>) => {
      return { ...s, ...newState };
    },
    initialState,
  );

  const controller = new AbortController();

  const loadBarRaisers = async () => {
    try {
      dispatch({ isLoading: true });
      let barRaisers: UserProfile[];
      if (cache.current) {
        barRaisers = cache.current;
      } else {
        const { userProfiles } = await mplService.getBarRaiserProfilesByTechDomain(
          techDomain,
          controller,
        );
        // @ts-expect-error TS7006 - strict migration
        barRaisers = userProfiles.map((up) => ({ userId: up.userId, name: up.userName }));
      }
      dispatch({ isLoading: false, barRaisers });
    } catch (e) {
      if (!controller.signal.aborted) {
        // @ts-expect-error TS18046 - strict migration
        dispatch({ isLoading: false, barRaisers: [], error: e.msg });
      }
    }
  };

  useEffect(() => {
    loadBarRaisers();
    return () => controller.abort();
  }, [techDomain]); // eslint-disable-line react-hooks/exhaustive-deps

  return { ...state };
}
